import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getPayrolls, processPayroll, bulkProcessPayroll, approvePayroll, markPayrollPaid,
  getPayslipPdf, getAdvances, requestAdvance, reviewAdvance,
  getArrears, createArrear, reviewArrear, markArrearPaid,
  getClients, getDeployments, getCandidates
} from '../services/api';
import {
  CreditCard, Plus, Download, CheckCircle, X, Zap, History, Wallet, ChevronDown, ChevronUp, Search
} from 'lucide-react';
import toast from 'react-hot-toast';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const FMT    = (n) => Number(n||0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const FMTN   = (n) => Number(n||0).toLocaleString('en-IN');

const statusBadge = (s) =>
  s === 'paid'      ? 'bg-green-50 text-green-700' :
  s === 'approved'  ? 'bg-blue-50 text-blue-700'   :
  s === 'processed' ? 'bg-amber-50 text-amber-700'  :
  s === 'pending'   ? 'bg-yellow-50 text-yellow-700':
  s === 'rejected'  ? 'bg-red-50 text-red-600'      :
  'bg-gray-50 text-gray-500';

const curYear = new Date().getFullYear();
const YEARS   = [curYear - 1, curYear, curYear + 1];

// ════════════════════════════════════════
//  PROCESS PAYROLL MODAL
// ════════════════════════════════════════
const ProcessModal = ({ onClose }) => {
  const qc   = useQueryClient();
  const [form, setForm] = useState({
    deployment_id: '', month: new Date().getMonth() + 1,
    year: curYear, bonus: 0, other_deductions: 0
  });
  const [preview, setPreview] = useState(null);

  const { data: deps = [] } = useQuery({
    queryKey: ['deps-active-pay'],
    queryFn : () => getDeployments({ status: 'active', limit: 200 }).then(r => r.data.data || [])
  });

  const mut = useMutation({
    mutationFn: processPayroll,
    onSuccess : (r) => { setPreview(r.data.data); toast.success('Payroll processed!'); qc.invalidateQueries(['payrolls']); },
    onError   : (e) => toast.error(e.response?.data?.message || 'Failed')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Process Payroll</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        {!preview ? (
          <form onSubmit={e => { e.preventDefault(); mut.mutate({ ...form, month: +form.month, year: +form.year, bonus: +form.bonus || 0, other_deductions: +form.other_deductions || 0 }); }} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Deployment *</label>
              <select value={form.deployment_id} onChange={e => setForm({...form, deployment_id: e.target.value})} required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="">Select...</option>
                {deps.map(d => (
                  <option key={d.id} value={d.id}>{d.candidates?.first_name} {d.candidates?.last_name} — {d.clients?.company_name}</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Month</label>
                <select value={form.month} onChange={e => setForm({...form, month: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Year</label>
                <select value={form.year} onChange={e => setForm({...form, year: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Bonus (₹)</label>
                <input type="number" value={form.bonus} onChange={e => setForm({...form, bonus: e.target.value})} min="0"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1">Other Deductions (₹)</label>
                <input type="number" value={form.other_deductions} onChange={e => setForm({...form, other_deductions: e.target.value})} min="0"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            </div>
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
              <button type="submit" disabled={mut.isPending} className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {mut.isPending ? 'Processing...' : 'Process'}
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-green-600 mb-2"><CheckCircle size={20} /><span className="font-medium">Processed!</span></div>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              {[['Payroll No', preview.payroll_no], ['Gross Salary', `₹${FMT(preview.gross_salary)}`],
                ['PF', `- ₹${FMT(preview.pf_employee)}`], ['ESI', `- ₹${FMT(preview.esic_employee)}`],
                ['Prof. Tax', `- ₹${FMT(preview.professional_tax)}`], ['Net Salary', `₹${FMT(preview.net_salary)}`]
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between">
                  <span className="text-gray-500">{k}</span>
                  <span className={k === 'Net Salary' ? 'font-semibold text-green-600' : 'font-medium'}>{v}</span>
                </div>
              ))}
            </div>
            <button onClick={onClose} className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">Done</button>
          </div>
        )}
      </div>
    </div>
  );
};

// ════════════════════════════════════════
//  PAY MODAL
// ════════════════════════════════════════
const PayModal = ({ payroll, onClose }) => {
  const qc  = useQueryClient();
  const [form, setForm] = useState({ payment_mode: 'bank_transfer', payment_reference: '' });

  const approveMut = useMutation({
    mutationFn: () => approvePayroll(payroll.id),
    onSuccess : () => { toast.success('Approved'); qc.invalidateQueries(['payrolls']); }
  });
  const payMut = useMutation({
    mutationFn: () => markPayrollPaid(payroll.id, form),
    onSuccess : () => { toast.success('Marked as paid'); qc.invalidateQueries(['payrolls']); onClose(); }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Pay Salary — {payroll.payroll_no}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="bg-gray-50 rounded-xl p-4 mb-4 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Gross</span><span>₹{FMT(payroll.gross_salary)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Deductions</span><span className="text-red-600">- ₹{FMT(payroll.total_deductions)}</span></div>
          <div className="flex justify-between font-semibold border-t pt-1 mt-1"><span>Net Payable</span><span className="text-green-600">₹{FMT(payroll.net_salary)}</span></div>
        </div>
        <div className="space-y-3 mb-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Payment Mode</label>
            <select value={form.payment_mode} onChange={e => setForm({...form, payment_mode: e.target.value})}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="bank_transfer">Bank Transfer (NEFT/RTGS)</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="upi">UPI</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reference / UTR</label>
            <input value={form.payment_reference} onChange={e => setForm({...form, payment_reference: e.target.value})}
              placeholder="Transaction ID or cheque number"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
        </div>
        <div className="flex gap-3">
          {payroll.status === 'processed' && (
            <button onClick={() => approveMut.mutate()} disabled={approveMut.isPending}
              className="flex-1 px-4 py-2 border border-blue-300 text-blue-600 rounded-lg text-sm font-medium hover:bg-blue-50 disabled:opacity-50">
              {approveMut.isPending ? '...' : 'Approve Only'}
            </button>
          )}
          <button onClick={() => payMut.mutate()} disabled={payMut.isPending}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50">
            {payMut.isPending ? 'Processing...' : 'Mark as Paid'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ════════════════════════════════════════
//  PAYROLL REGISTER TAB
// ════════════════════════════════════════
const PayrollTab = () => {
  const [month,   setMonth]   = useState(new Date().getMonth() + 1);
  const [year,    setYear]    = useState(curYear);
  const [status,  setStatus]  = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [payingId, setPayingId] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['payrolls', month, year, status],
    queryFn : () => getPayrolls({ month, year, status, limit: 100 }).then(r => r.data)
  });

  const downloadPayslip = async (pay) => {
    try {
      const res  = await getPayslipPdf(pay.id);
      const url  = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a    = document.createElement('a');
      a.href = url; a.download = `Payslip-${pay.payroll_no}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download'); }
  };

  const payrollBeingPaid = payingId ? data?.data?.find(p => p.id === payingId) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex gap-2">
          <select value={month} onChange={e => setMonth(+e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(+e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Status</option>
            {['draft','processed','approved','paid','on_hold'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
        <div className="ml-auto">
          <button onClick={() => setShowAdd(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
            <Plus size={15} />
            Process Payroll
          </button>
        </div>
      </div>

      {/* Totals */}
      {data?.totals && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Gross Salary',  v: data.totals.gross_salary, c: 'text-gray-900' },
            { label: 'Net Salary',    v: data.totals.net_salary,   c: 'text-green-600' },
            { label: 'PF (Employer)', v: data.totals.pf_employer,  c: 'text-blue-600'  }
          ].map(({ label, v, c }) => (
            <div key={label} className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs text-gray-400 mb-1">{label}</p>
              <p className={`text-xl font-semibold ${c}`}>₹{FMTN(Math.round(v || 0))}</p>
            </div>
          ))}
        </div>
      )}

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading payroll...</div>
        ) : !data?.data?.length ? (
          <div className="text-center py-12">
            <CreditCard size={32} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No payroll records for this period.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{['Employee','Period','Paid Days','Gross','Net Salary','Status','Actions'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.data.map(pay => (
                <tr key={pay.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{pay.candidates?.first_name} {pay.candidates?.last_name}</p>
                    <p className="text-xs text-gray-400">{pay.candidates?.employee_code} · {pay.clients?.company_name}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{MONTHS[pay.month-1]} {pay.year}</td>
                  <td className="px-4 py-3 text-gray-600">{Number(pay.paid_days).toFixed(1)} / {pay.working_days}</td>
                  <td className="px-4 py-3 text-gray-900">₹{FMTN(Math.round(pay.gross_salary))}</td>
                  <td className="px-4 py-3 font-semibold text-green-700">₹{FMTN(Math.round(pay.net_salary))}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(pay.status)}`}>{pay.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {pay.status !== 'paid' && (
                        <button onClick={() => setPayingId(pay.id)}
                          className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded-lg hover:bg-green-100">
                          Pay
                        </button>
                      )}
                      <button onClick={() => downloadPayslip(pay)}
                        className="text-gray-400 hover:text-blue-600">
                        <Download size={15} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd         && <ProcessModal onClose={() => setShowAdd(false)} />}
      {payrollBeingPaid && <PayModal payroll={payrollBeingPaid} onClose={() => setPayingId(null)} />}
    </div>
  );
};

// ════════════════════════════════════════
//  BULK PROCESS TAB
// ════════════════════════════════════════
const BulkTab = () => {
  const qc = useQueryClient();
  const [month,    setMonth]    = useState(new Date().getMonth() + 1);
  const [year,     setYear]     = useState(curYear);
  const [clientId, setClientId] = useState('');
  const [result,   setResult]   = useState(null);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list'],
    queryFn : () => getClients({ limit: 200 }).then(r => r.data.data || [])
  });

  const mut = useMutation({
    mutationFn: () => bulkProcessPayroll({ month, year, client_id: clientId || undefined }),
    onSuccess : (r) => {
      setResult(r.data.data);
      toast.success(r.data.message || 'Bulk run complete');
      qc.invalidateQueries(['payrolls']);
    },
    onError: (e) => toast.error(e.response?.data?.message || 'Bulk run failed')
  });

  return (
    <div className="max-w-xl space-y-5">
      <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm text-blue-700">
        <strong>Bulk Payroll Run</strong> — processes all active deployments for the selected period. Already-paid records are automatically skipped.
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-5 space-y-4">
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Period</label>
          <div className="flex gap-2">
            <select value={month} onChange={e => setMonth(+e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
            </select>
            <select value={year} onChange={e => setYear(+e.target.value)}
              className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Client (optional — leave blank for all)</label>
          <select value={clientId} onChange={e => setClientId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">All Clients</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
          </select>
        </div>

        <button onClick={() => { setResult(null); mut.mutate(); }} disabled={mut.isPending}
          className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
          <Zap size={16} />
          {mut.isPending ? 'Running Bulk Payroll...' : `Run Bulk Payroll — ${MONTHS[month-1]} ${year}`}
        </button>
      </div>

      {result && (
        <div className="bg-white rounded-2xl border border-gray-100 p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Run Results</h3>
          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center bg-green-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-green-600">{result.processed}</p>
              <p className="text-xs text-green-700">Processed</p>
            </div>
            <div className="text-center bg-gray-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-gray-500">{result.skipped}</p>
              <p className="text-xs text-gray-500">Skipped</p>
            </div>
            <div className="text-center bg-red-50 rounded-xl p-3">
              <p className="text-2xl font-bold text-red-600">{result.failed}</p>
              <p className="text-xs text-red-600">Failed</p>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-medium text-red-600">Errors:</p>
              {result.errors.map((e, i) => (
                <p key={i} className="text-xs text-red-500 bg-red-50 rounded px-2 py-1">{e.error}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════
//  ARREARS TAB
// ════════════════════════════════════════
const CreateArrearModal = ({ onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    deployment_id: '', from_month: 1, from_year: curYear - 1, to_month: 12, to_year: curYear - 1,
    old_salary: '', new_salary: '', reason: ''
  });

  const { data: deps = [] } = useQuery({
    queryKey: ['deps-active-arr'],
    queryFn : () => getDeployments({ status: 'active', limit: 200 }).then(r => r.data.data || [])
  });

  const oldS   = parseFloat(form.old_salary) || 0;
  const newS   = parseFloat(form.new_salary) || 0;
  const months = form.from_year === form.to_year
    ? form.to_month - form.from_month + 1
    : (form.to_year - form.from_year) * 12 + (form.to_month - form.from_month) + 1;
  const arrAmount = Math.max(0, (newS - oldS) * months);

  const mut = useMutation({
    mutationFn: () => createArrear({ ...form, from_month: +form.from_month, from_year: +form.from_year, to_month: +form.to_month, to_year: +form.to_year, old_salary: oldS, new_salary: newS }),
    onSuccess : () => { toast.success('Arrear created'); qc.invalidateQueries(['arrears']); onClose(); },
    onError   : (e) => toast.error(e.response?.data?.message || 'Failed')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Create Arrear</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Deployment *</label>
            <select value={form.deployment_id} onChange={e => setForm({...form, deployment_id: e.target.value})} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select...</option>
              {deps.map(d => <option key={d.id} value={d.id}>{d.candidates?.first_name} {d.candidates?.last_name} — {d.clients?.company_name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From (Month/Year)</label>
              <div className="flex gap-1">
                <select value={form.from_month} onChange={e => setForm({...form, from_month: +e.target.value})}
                  className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <select value={form.from_year} onChange={e => setForm({...form, from_year: +e.target.value})}
                  className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To (Month/Year)</label>
              <div className="flex gap-1">
                <select value={form.to_month} onChange={e => setForm({...form, to_month: +e.target.value})}
                  className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {MONTHS.map((m,i) => <option key={i} value={i+1}>{m}</option>)}
                </select>
                <select value={form.to_year} onChange={e => setForm({...form, to_year: +e.target.value})}
                  className="flex-1 px-2 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                  {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Old Monthly Salary (₹)</label>
              <input type="number" value={form.old_salary} onChange={e => setForm({...form, old_salary: e.target.value})} min="0"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">New Monthly Salary (₹)</label>
              <input type="number" value={form.new_salary} onChange={e => setForm({...form, new_salary: e.target.value})} min="0"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          {arrAmount > 0 && (
            <div className="bg-amber-50 rounded-xl px-4 py-3 text-sm">
              <span className="text-amber-700">Arrear Amount: </span>
              <strong className="text-amber-800">₹{FMTN(arrAmount)}</strong>
              <span className="text-amber-600 ml-2">({months} month{months !== 1 ? 's' : ''} × ₹{FMTN(newS - oldS)})</span>
            </div>
          )}
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
            <input value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} placeholder="e.g. Salary revision effective Apr 2025"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.deployment_id || arrAmount <= 0}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mut.isPending ? 'Creating...' : 'Create Arrear'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ArrearsTab = () => {
  const qc = useQueryClient();
  const [showAdd, setShowAdd] = useState(false);
  const [status,  setStatus]  = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['arrears', status],
    queryFn : () => getArrears({ status: status || undefined, limit: 100 }).then(r => r.data.data)
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, approved }) => reviewArrear(id, { approved }),
    onSuccess : () => { toast.success('Updated'); qc.invalidateQueries(['arrears']); }
  });

  const payMut = useMutation({
    mutationFn: ({ id }) => markArrearPaid(id, { month: new Date().getMonth() + 1, year: curYear }),
    onSuccess : () => { toast.success('Marked paid'); qc.invalidateQueries(['arrears']); }
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          {['pending','approved','rejected','paid'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => setShowAdd(true)}
          className="ml-auto flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={15} />
          Create Arrear
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : !data?.items?.length ? (
          <div className="text-center py-12">
            <History size={28} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No arrear records.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{['Employee','Period','Old → New','Arrear Amount','Reason','Status','Actions'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.items.map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{a.candidates?.first_name} {a.candidates?.last_name}</p>
                    <p className="text-xs text-gray-400">{a.candidates?.employee_code}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {MONTHS[a.from_month-1]} {a.from_year} — {MONTHS[a.to_month-1]} {a.to_year}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    ₹{FMTN(a.old_salary)} → ₹{FMTN(a.new_salary)}
                  </td>
                  <td className="px-4 py-3 font-semibold text-amber-700">₹{FMT(a.arrear_amount)}</td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{a.reason || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(a.status)}`}>{a.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      {a.status === 'pending' && (
                        <>
                          <button onClick={() => reviewMut.mutate({ id: a.id, approved: true })}
                            className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100">Approve</button>
                          <button onClick={() => reviewMut.mutate({ id: a.id, approved: false })}
                            className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100">Reject</button>
                        </>
                      )}
                      {a.status === 'approved' && (
                        <button onClick={() => payMut.mutate({ id: a.id })}
                          className="text-xs px-2 py-1 bg-blue-50 text-blue-700 rounded hover:bg-blue-100">Mark Paid</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {showAdd && <CreateArrearModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

// ════════════════════════════════════════
//  LOANS & ADVANCES TAB
// ════════════════════════════════════════
const AdvanceModal = ({ onClose }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    candidate_id: '', loan_type: 'advance', amount: '', reason: '', recovery_months: 1
  });

  const { data: candidates = [] } = useQuery({
    queryKey: ['cands-adv'],
    queryFn : () => getCandidates({ status: 'deployed', limit: 200 }).then(r => r.data.data || [])
  });

  const mut = useMutation({
    mutationFn: () => requestAdvance({ ...form, amount: parseFloat(form.amount), recovery_months: +form.recovery_months }),
    onSuccess : () => { toast.success('Request submitted'); qc.invalidateQueries(['advances']); onClose(); },
    onError   : (e) => toast.error(e.response?.data?.message || 'Failed')
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-semibold text-gray-900">Request Advance / Loan</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Employee *</label>
            <select value={form.candidate_id} onChange={e => setForm({...form, candidate_id: e.target.value})} required
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">Select...</option>
              {candidates.map(c => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.employee_code})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Type</label>
              <select value={form.loan_type} onChange={e => setForm({...form, loan_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="advance">Salary Advance</option>
                <option value="loan">Loan</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Amount (₹) *</label>
              <input type="number" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} min="1" required
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Recovery in (months) *</label>
            <input type="number" value={form.recovery_months} onChange={e => setForm({...form, recovery_months: e.target.value})} min="1" max="24"
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
            {form.amount && form.recovery_months > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                Monthly deduction: ₹{FMTN(Math.ceil(parseFloat(form.amount) / form.recovery_months))}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Reason</label>
            <textarea value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} rows={2}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancel</button>
            <button onClick={() => mut.mutate()} disabled={mut.isPending || !form.candidate_id || !form.amount}
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
              {mut.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const AdvancesTab = () => {
  const qc = useQueryClient();
  const [showAdd,   setShowAdd]   = useState(false);
  const [status,    setStatus]    = useState('');
  const [loanType,  setLoanType]  = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['advances', status, loanType],
    queryFn : () => getAdvances({ status: status || undefined, loan_type: loanType || undefined, limit: 100 }).then(r => r.data.data)
  });

  const reviewMut = useMutation({
    mutationFn: ({ id, approved }) => reviewAdvance(id, { approved }),
    onSuccess : () => { toast.success('Updated'); qc.invalidateQueries(['advances']); }
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={status} onChange={e => setStatus(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Status</option>
          {['pending','approved','rejected','recovered'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={loanType} onChange={e => setLoanType(e.target.value)}
          className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
          <option value="">All Types</option>
          <option value="advance">Advance</option>
          <option value="loan">Loan</option>
        </select>
        <button onClick={() => setShowAdd(true)}
          className="ml-auto flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700">
          <Plus size={15} />
          New Request
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="text-center py-12 text-gray-400">Loading...</div>
        ) : !data?.items?.length ? (
          <div className="text-center py-12">
            <Wallet size={28} className="mx-auto text-gray-200 mb-3" />
            <p className="text-sm text-gray-400">No loan/advance records.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{['Employee','Type','Amount','Recovery','Reason','Status','Actions'].map(h => (
                <th key={h} className="text-left text-xs font-medium text-gray-400 px-4 py-3">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data.items.map(a => (
                <tr key={a.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-900">{a.candidates?.first_name} {a.candidates?.last_name}</p>
                    <p className="text-xs text-gray-400">{a.candidates?.employee_code} · {a.deployments?.clients?.company_name}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${a.loan_type === 'loan' ? 'bg-purple-50 text-purple-700' : 'bg-blue-50 text-blue-700'}`}>
                      {a.loan_type}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">₹{FMT(a.amount)}</td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {a.recovery_months} month{a.recovery_months > 1 ? 's' : ''}<br />
                    <span className="text-gray-400">₹{FMTN(Math.ceil(a.amount / a.recovery_months))}/mo</span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs max-w-xs truncate">{a.reason || '—'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(a.status)}`}>{a.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    {a.status === 'pending' && (
                      <div className="flex gap-1">
                        <button onClick={() => reviewMut.mutate({ id: a.id, approved: true })}
                          className="text-xs px-2 py-1 bg-green-50 text-green-700 rounded hover:bg-green-100">Approve</button>
                        <button onClick={() => reviewMut.mutate({ id: a.id, approved: false })}
                          className="text-xs px-2 py-1 bg-red-50 text-red-600 rounded hover:bg-red-100">Reject</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAdd && <AdvanceModal onClose={() => setShowAdd(false)} />}
    </div>
  );
};

// ════════════════════════════════════════
//  MAIN PAGE
// ════════════════════════════════════════
const Payroll = () => {
  const [tab, setTab] = useState('register');

  const tabs = [
    { id: 'register', label: 'Payroll Register' },
    { id: 'bulk',     label: 'Bulk Process'     },
    { id: 'arrears',  label: 'Arrears'          },
    { id: 'advances', label: 'Loans & Advances' }
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-gray-900">Payroll</h1>
        <p className="text-sm text-gray-400 mt-0.5">Process salaries, arrears, loans and advances</p>
      </div>

      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition
              ${tab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'register' && <PayrollTab />}
      {tab === 'bulk'     && <BulkTab />}
      {tab === 'arrears'  && <ArrearsTab />}
      {tab === 'advances' && <AdvancesTab />}
    </div>
  );
};

export default Payroll;
